// BANF-Verlauf lesbar machen: sammelzettel_history (old_data/new_data JSON) → Ereigniszeilen je Position/Bestellung.
// Audit-Formen (api/src/routes/sammelzettel.ts):
//   INSERT                                     → BANF angelegt
//   { pos_neu: {id,…} }                        → Position angelegt
//   { pos: row, aktion?: 'rueckfrage'|'rueckfrage-zurueck'|'freigeben'|'ablehnen' } (old: { pos: alt }) → Statuswechsel/Rückfrage
//   { bestellt: [{lieferant,bestellnr,positionen:[ids]}] } → Bestellung angelegt
//   { aus_bedarf: [ids] }                      → Bedarfe übernommen
import type { BedarfHistorie } from './api';

export interface VerlaufEreignis { id: number; wann: string; wer: string; text: string; posIds: number[] }
type J = Record<string, unknown>;
const parse = (s: string | null): J | null => { try { return s ? JSON.parse(s) as J : null; } catch { return null; } };
const kurz = (t: unknown, n = 80) => { const x = String(t ?? '').trim(); return x.length > n ? x.slice(0, n) + '…' : x; };

export function banfEreignisse(rows: BedarfHistorie[], posNr: (id: number) => string = id => `#${id}`): VerlaufEreignis[] {
  const out: VerlaufEreignis[] = [];
  for (const h of rows) {
    const e = { id: h.id, wann: h.changed_at, wer: h.user_name ?? '—' };
    const neu = parse(h.new_data); const alt = parse(h.old_data);
    if (h.action === 'INSERT') { out.push({ ...e, text: 'BANF angelegt', posIds: [] }); continue; }
    if (h.action === 'DELETE') { out.push({ ...e, text: `BANF storniert${neu && neu['storno_grund'] ? `: ${kurz(neu['storno_grund'], 120)}` : ''}`, posIds: [] }); continue; }
    if (!neu) { out.push({ ...e, text: 'geändert', posIds: [] }); continue; }
    if (Array.isArray(neu['bestellt'])) {
      for (const g of neu['bestellt'] as { lieferant?: string; bestellnr?: string; positionen?: number[] }[]) {
        const ids = (g.positionen ?? []).map(Number);
        out.push({ ...e, text: `Bestellung bei ${g.lieferant ?? '—'} angelegt (Best.-Nr. ${g.bestellnr ?? '—'}) · ${ids.map(posNr).join(', ')}`, posIds: ids });
      }
      continue;
    }
    if (neu['pos_neu'] && typeof neu['pos_neu'] === 'object') {
      const p = neu['pos_neu'] as J; out.push({ ...e, text: `Position angelegt: ${kurz(p['bezeichnung'])}`, posIds: [Number(p['id'])] }); continue;
    }
    if (Array.isArray(neu['aus_bedarf'])) { out.push({ ...e, text: `${(neu['aus_bedarf'] as unknown[]).length} Blech-Bedarf(e) übernommen`, posIds: [] }); continue; }
    if (neu['pos'] && typeof neu['pos'] === 'object') {
      const p = neu['pos'] as J; const a = (alt?.['pos'] ?? {}) as J; const id = Number(p['id']);
      const nr = posNr(id); const aktion = String(neu['aktion'] ?? '');
      let text = '';
      if (aktion === 'rueckfrage') text = `${nr}: Frage an den Chef — „${kurz(p['frage_text'], 120)}“`;
      else if (aktion === 'rueckfrage-zurueck') text = `${nr}: Frage an den Chef zurückgezogen`;
      else if (aktion === 'freigeben') text = `${nr}: vom Chef freigegeben (${p['entscheid_von'] ?? '—'})${p['entscheid_text'] ? ` — „${kurz(p['entscheid_text'], 120)}“` : ''}`;
      else if (aktion === 'ablehnen') text = `${nr}: vom Chef abgelehnt (${p['entscheid_von'] ?? '—'}) — „${kurz(p['entscheid_text'], 120)}“`;
      else if (a['status'] !== p['status']) {
        text = p['status'] === 'geliefert' ? `${nr}: Wareneingang ✓`
          : p['status'] === 'offen' && a['status'] === 'bestellt' ? `${nr}: Bestellung zurückgesetzt`
          : p['status'] === 'storniert' ? `${nr}: Position storniert` : `${nr}: Status ${a['status']} → ${p['status']}`;
      } else text = `${nr}: geändert`;
      out.push({ ...e, text, posIds: [id] });
      continue;
    }
    out.push({ ...e, text: 'geändert', posIds: [] });
  }
  return out;
}
